import { FaPlane, FaTruck } from 'react-icons/fa';
import Icon from '../ui/Icon';
import type { JourneyResponse, PipelineStage } from '../../types/tracking';
import {
  iconForName,
  fmtDate,
  fmtTime,
  fmtRelativeTime,
  prettyStatus,
} from './tracking-utils';
import styles from './JourneyMilestones.module.css';

/**
 * Customer-facing tracking result — an Amazon / Flipkart-style view built
 * entirely from the data the public API already resolves:
 *   • status hero    ← journey.current + routing + progress
 *   • milestone stepper (vertical) ← journey.pipeline (done | active |
 *     upcoming; the backend already groups events into stages and varies them
 *     by shipment type, so the UI just renders what it's given)
 * No PII is present in the response (no sender/receiver names/phones/addresses).
 */

type Tone = 'brand' | 'green' | 'amber';

function toneForStatus(status: string | null | undefined, delivered: boolean): Tone {
  const s = String(status ?? '').toLowerCase();
  if (delivered || s.includes('deliver')) return 'green';
  if (
    /(exception|custom|held|rto|return|damage|lost|fail|attempt|delay|reschedul|pending)/.test(
      s,
    )
  )
    return 'amber';
  return 'brand';
}

export default function JourneyMilestones({
  journey,
  live,
  showStepper = true,
}: {
  journey: JourneyResponse;
  live?: boolean;
  /** The fixed ladder. Off when the page shows the real event list below —
   *  the same journey told twice, once honestly and once approximately, only
   *  invites the customer to spot the difference. */
  showStepper?: boolean;
}) {
  // Only relevant milestones — the backend marks stages that don't apply to
  // this shipment as "skipped".
  const stages: PipelineStage[] = (journey.pipeline ?? []).filter(
    (s) => s.status !== 'skipped',
  );

  const lastStage = stages[stages.length - 1];
  const delivered =
    !!lastStage &&
    lastStage.status === 'done' &&
    /deliver/i.test(lastStage.label);

  // Current position = the active stage, else the last completed one.
  const activeIdx = (() => {
    const ai = stages.findIndex((s) => s.status === 'active');
    if (ai >= 0) return ai;
    let last = -1;
    stages.forEach((s, i) => {
      if (s.status === 'done') last = i;
    });
    return last;
  })();

  const statusText =
    journey.current?.statusLabel ??
    (journey.current?.status ? prettyStatus(journey.current.status) : null) ??
    (activeIdx >= 0 ? stages[activeIdx].label : null) ??
    'Booked';

  // Recent non-milestone status updates (Delayed / Held / On track…) with
  // their messages, surfaced at the current node so they appear IN the timeline
  // — not just as the hero headline. Newest-first from the backend.
  const statusUpdates = journey.statusUpdates ?? [];

  const tone = toneForStatus(journey.current?.status ?? statusText, delivered);
  const toneClass =
    tone === 'green'
      ? styles.toneGreen
      : tone === 'amber'
        ? styles.toneAmber
        : styles.toneBrand;

  const eta = journey.current?.expectedDeliveryAt ?? null;
  const subLine = eta
    ? `Estimated delivery · ${fmtDate(eta)}`
    : journey.lastUpdatedAt
      ? `Updated ${fmtRelativeTime(journey.lastUpdatedAt)}`
      : null;

  const routing = journey.routing;
  const from =
    routing?.displayFrom ??
    routing?.originText ??
    routing?.originBranch?.name ??
    null;
  const to =
    routing?.displayTo ??
    routing?.destinationText ??
    routing?.destinationBranch?.name ??
    null;
  const isAir =
    journey.type === 'MAWB' || !!journey.current?.mawb || !!routing?.hubBranch;

  return (
    <div className={`${styles.card} ${toneClass}`}>
      {/* ---- Status hero ---- */}
      <div className={styles.hero}>
        <div className={styles.heroTop}>
          <span className={styles.eyebrow}>Tracking · {journey.type}</span>
          {live ? (
            <span className={styles.live}>
              <span className={styles.liveDot} /> Live
            </span>
          ) : null}
        </div>
        <p className={styles.identifier}>{journey.identifier}</p>
        <p className={styles.statusHeadline}>{statusText}</p>
        {subLine ? <p className={styles.subLine}>{subLine}</p> : null}

        {from || to ? (
          <div className={styles.route}>
            <span className={styles.routeEnd}>{from ?? '—'}</span>
            <span className={styles.routeMid} aria-hidden="true">
              <span className={styles.routeLine} />
              <Icon icon={isAir ? FaPlane : FaTruck} />
              <span className={styles.routeLine} />
            </span>
            <span className={`${styles.routeEnd} ${styles.routeTo}`}>
              {to ?? '—'}
            </span>
          </div>
        ) : null}
      </div>

      {/* ---- Milestone stepper (vertical) ---- */}
      {showStepper && stages.length > 0 ? (
        <div className={styles.stepper}>
          {stages.map((s, i) => {
            const nodeClass =
              s.status === 'done'
                ? styles.nodeDone
                : s.status === 'active'
                  ? styles.nodeActive
                  : styles.nodeUpcoming;
            const reached = s.status === 'done' || s.status === 'active';
            return (
              <div
                key={s.key}
                className={`${styles.step} ${i < activeIdx ? styles.railBelowFilled : ''}`}
              >
                <span className={`${styles.node} ${nodeClass}`}>
                  <Icon icon={iconForName(s.icon)} />
                </span>
                <span
                  className={`${styles.stepLabel} ${
                    s.status === 'active'
                      ? styles.stepLabelActive
                      : s.status === 'upcoming'
                        ? styles.stepLabelUpcoming
                        : ''
                  }`}
                >
                  {s.label}
                </span>
                {s.reachedAt && reached ? (
                  <span className={styles.stepDate}>{fmtDate(s.reachedAt)}</span>
                ) : null}
                {s.branch?.name && reached ? (
                  <span className={styles.stepBranch}>{s.branch.name}</span>
                ) : null}
                {reached && (s.courierName || s.trackingNumber) ? (
                  <span className={styles.stepCourier}>
                    {s.courierName ? `Courier: ${s.courierName}` : 'Third-party courier'}
                    {s.trackingNumber ? ` · Tracking ${s.trackingNumber}` : ''}
                  </span>
                ) : null}
                {s.status === 'active' ? (
                  <span className={styles.nowBadge}>Now</span>
                ) : null}
                {s.status === 'active' && statusUpdates.length > 0 ? (
                  <div className={styles.updates}>
                    {statusUpdates.map((u, ui) => (
                      <div key={ui} className={styles.updateRow}>
                        <div className={styles.updateHead}>
                          <span className={styles.updateStatus}>{u.status}</span>
                          {u.at ? (
                            <span className={styles.updateWhen}>
                              {fmtDate(u.at)} · {fmtTime(u.at)}
                            </span>
                          ) : null}
                        </div>
                        {u.message ? (
                          <p className={styles.updateMsg}>{u.message}</p>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
